import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchFlashDealByCode, clearFormData, fetchAllFlashDeals, fetchAllActiveFlashDeals, fetchInactiveFlashDeals, fetchCurrentFlashingDeals } from '../../../store/slices/flashDealsSlice';
import { AppDispatch, RootState } from '../../../store/store';
import FlashDealForm from './FlashDealForm';
import { Box, CircularProgress, Typography, Alert, Collapse, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import format from 'date-fns/format';
 
interface FlashDealContainerProps {
  mode: 'create' | 'edit';
  dealCode?: string;
  onSuccess?: (data: any) => void;
  onCancel?: () => void;
  onCloseTab?: (tabId: string) => void;
}
 
const FlashDealContainer: React.FC<FlashDealContainerProps> = ({
  mode,
  dealCode,
  onSuccess,
  onCancel,
  onCloseTab

}) => {
  const dispatch = useDispatch<AppDispatch>();
  const fetchBycodeData = useSelector((state: RootState) => state.flashDeals.currentFlashDeal);
  const loading = useSelector((state: RootState) => state.flashDeals.loading);
  const error = useSelector((state: RootState) => state.flashDeals.error);
  const currentFilter = useSelector((state: RootState) => state.flashDeals.currentFilter);
  const [isReady, setIsReady] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error'>('success');
 
  // Fetch data for edit mode
  useEffect(() => {
    if (mode === 'edit' && dealCode) {
      setIsReady(false);
      dispatch(fetchFlashDealByCode(dealCode)).then(() => {
        setIsReady(true);
      });
    } else if (mode === 'create') {
      setIsReady(true);
    }
  }, [mode, dealCode, dispatch]);
 
  // Helper function to refresh flash deals based on current filter
  const refreshFlashDealsWithFilter = async () => {
    const currentDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
    
    if (currentFilter === 'active') {
      await dispatch(fetchAllActiveFlashDeals());
    } else if (currentFilter === 'inactive') {
      await dispatch(fetchInactiveFlashDeals());
    } else if (currentFilter === 'currentFlashing') {
      await dispatch(fetchCurrentFlashingDeals(currentDateTime));
    } else {
      await dispatch(fetchAllFlashDeals());
    }
  };

  const handleSuccess = (data: any) => {
    const { result, mode: operationMode } = data;

    if (operationMode === 'edit') {
      const message = result?.message || 'Operation completed successfully';
      setSnackbarMessage(message);
      setAlertSeverity('success');
      setSnackbarOpen(true);

      setTimeout(() => {
        setSnackbarOpen(false);
      }, 5000);
    }

    if (operationMode === 'create') {
      dispatch(clearFormData());
      refreshFlashDealsWithFilter();
    } else if (operationMode === 'edit') {
      refreshFlashDealsWithFilter();
    }
   
    // Pass the full data including mode and result to parent
    if (onSuccess) {
      onSuccess({
        mode: operationMode,
        result: result
      });
    }
  };

  const handleError = (error: any) => {
    // Use error message from API response
    const message = error?.message || error || 'An error occurred. Please try again.';
    setSnackbarMessage(message);
    setAlertSeverity('error');
    setSnackbarOpen(true);
    setTimeout(() => {
    setSnackbarOpen(false);
  }, 5000);
  };
 
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };
 
  const handleCancel = () => {
    if (mode === 'create') {
      dispatch(clearFormData());
    }
    if (onCancel) onCancel();
  };
 
  // Loading state
  if (!isReady || (mode === 'edit' && loading)) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh" flexDirection="column">
        <CircularProgress color="primary" size={60} />
        <Typography variant="h6" color="primary" sx={{ mt: 2 }}>
          Loading flash deal...
        </Typography>
      </Box>
    );
  }
 
  // Error state
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh" flexDirection="column">
        <Typography variant="h6" color="error">
          {error}
        </Typography>
      </Box>
    );
  }
 
  return (
    <>
      <Collapse in={snackbarOpen}>
        <Alert
          severity={alertSeverity}
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleSnackbarClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          sx={{ mb: 2 }}
        >
          {snackbarMessage}
        </Alert>
      </Collapse>
      <FlashDealForm
        mode={mode}
        initialData={mode === 'edit' ? fetchBycodeData : undefined}
        onSuccess={handleSuccess}
        onError={handleError}
        onCancel={handleCancel}
        onCloseTab={onCloseTab}
      />
    </>
  );
};
 
export default FlashDealContainer;